/**
 * @file shared-active-issues-filter.component.ts
 * @author Muhammad Hamza
 * @date 2023-01-28
 * @description Component for filtering active issues based on various criteria.
 */

import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Floor } from 'src/app/floor-management/floor-management-interface';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { IFilterFloor } from 'src/app/shared/state-management/state-interfaces';
import { HelperService } from 'src/app/shared/utils/helper.service';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';
import { BuildingManagementService } from 'src/app/building-managment/building-management.service';
import { IBuilding } from 'src/app/building-managment/building-management-interfaces';
import { FloorManagementService } from 'src/app/floor-management/floor-management.service';
import { catchError, map, tap } from 'rxjs/operators';
import { eventCodes } from 'src/app/shared/utils/event-codes';
import {
  IActiveIssuesFilter,
  IEventCodes,
} from 'src/app/shared/models/data-interfaces';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-shared-active-issues-filter',
  templateUrl: './shared-active-issues-filter.component.html',
  styleUrls: ['./shared-active-issues-filter.component.scss'],
})
export class SharedActiveIssuesFilterComponent implements OnInit {
  // Properties
  stepper: number = 0;
  CONSTANTS = CONSTANTS;
  readonly LANG_CONST = LANG_CONST;
  filter!: IFilterFloor;
  floorIcon: string = 'assets/svg/ico-floor-filter.svg';
  buildingIcon: string = 'assets/svg/ico-building-gray.svg';
  wingIcon: string = 'assets/svg/ico-direction.svg';
  eventTypeIcon: string = 'assets/svg/ico-lock.svg';
  buildings!: IBuilding[];
  stepFlows: string[] = [
    this.translateService.instant('FILTER_BY'),
    this.translateService.instant('BUILDING'),
    this.translateService.instant('FLOOR'),
    this.translateService.instant('WING'),
    this.translateService.instant('EVENT_TYPE'),
  ];
  floors: Floor;
  siteId: string;
  wings: any;
  eventCodes: IEventCodes[] = eventCodes;

  buildingDescription: string[] = [];
  floorDescription: string[] = [];
  wingsDescription: string[] = [];
  eventDescription: string[] = [];
  buildingIds: string[] = [];
  floorIds: string[] = [];
  eventCodeIds: string[] = [];

  activeIssuesFilter!: IActiveIssuesFilter;
  filterInitialized: boolean = false;

  // Constructor
  constructor(
    public dialogRef: MatDialogRef<SharedActiveIssuesFilterComponent>,
    private helperService: HelperService,
    private localizationService: LocalizationService,
    @Inject(MAT_DIALOG_DATA)
    data: {
      siteId: string;
      activeIssuesFilter: IActiveIssuesFilter;
      filterInitialized: boolean;
    },
    public buildingManagementService: BuildingManagementService,
    public floorManagementService: FloorManagementService,
    private translateService: TranslateService
  ) {
    this.siteId = data?.siteId;
    const activeIssuesFilter = { ...data?.activeIssuesFilter };
    this.buildings = activeIssuesFilter?.buildings;
    this.floors = activeIssuesFilter?.floors;
    this.wings = activeIssuesFilter?.wings;
    this.eventCodes = activeIssuesFilter?.eventCodes;
    this.buildingDescription = activeIssuesFilter?.buildingDescription;
    this.floorDescription = activeIssuesFilter?.floorDescription;
    this.wingsDescription = activeIssuesFilter?.wingsDescription;
    this.eventDescription = activeIssuesFilter?.eventDescription;
    this.buildingIds = activeIssuesFilter?.buildingIds;
    this.floorIds = activeIssuesFilter?.floorIds;
    this.eventCodeIds = activeIssuesFilter?.eventCodeIds;
    this.filterInitialized = data?.filterInitialized;
  }

  // Lifecycle Hook
  ngOnInit(): void {
    this.localizationService.setupLocalization();
    if (!this.filterInitialized) {
      this.fetchAllBuiuldings();
      this.eventCodes = this.transformListData(
        this.eventCodes = this.eventCodes ? this.eventCodes.map((event) => ({
          ...event,
          EventDescription: this.translateService.instant(event.EventDescription),
        })) : [],
        'EventCode',
        'EventDescription'
      );
    }
  }

  // Methods

  /**
   * @description: #applyFilter function will apply the final changes
   *                to the filterFloor afterClosed subscriber.
   * @output { void }
   */
  applyFilter(): void {
    this.activeIssuesFilter = {
      ...this.activeIssuesFilter,
      buildings: this.buildings,
      floors: this.floors,
      wings: this.wings,
      eventCodes: this.eventCodes,
      buildingDescription: this.buildingDescription,
      floorDescription: this.floorDescription,
      wingsDescription: this.wingsDescription,
      eventDescription: this.eventDescription,
      buildingIds: this.buildingIds,
      floorIds: this.floorIds,
      eventCodeIds: this.eventCodeIds,
    };
    this.dialogRef?.close({
      activeIssuesFilter: this.activeIssuesFilter,
      filterInitialized: this.filterInitialized,
    });
  }

  /**
   * @description: resetBuildings function reset all the selected buildings.
   * @input { none }
   * @output { void }
   */
  resetBuildings() {
    this.buildings = this.buildings?.map((item: any) => {
      return { ...item, state: false };
    });
    this.buildingDescription = [];
    this.buildingIds = [];
  }

  /**
   * @description: resetFloors function reset all the selected floors.
   * @input { none }
   * @output { void }
   */
  resetFloors() {
    this.floors = this.floors?.map((item: any) => {
      return { ...item, state: false };
    });
    this.floorDescription = [];
    this.floorIds = [];
  }

  /**
   * @description: resetWings function reset all the selected wings.
   * @input { none }
   * @output { void }
   */
  resetWings() {
    this.wings = this.wings?.map((item: any) => {
      return { ...item, state: false };
    });
    this.wingsDescription = [];
  }

  /**
   * @description: resetWings function reset all the selected eventCodes.
   * @input { none }
   * @output { void }
   */
  resetEventCodes() {
    this.eventCodes = this.eventCodes?.map((item: any) => {
      return { ...item, state: false };
    });
    this.eventDescription = [];
    this.eventCodeIds = [];
  }

  /**
   * @description: resetFilter Function acepts type as parameter and decide which filter to be removed.
   * @input { string }
   * @param { type }
   * @output { void }
   */
  resetFilter(step: number) {
    switch (this.stepFlows[step]) {
      case this.translateService.instant('FILTER_BY'): {
        this.resetBuildings();
        this.resetFloors();
        this.resetWings();
        this.resetEventCodes();
        break;
      }

      case this.translateService.instant('FLOOR'): {
        this.resetFloors();
        break;
      }

      case this.translateService.instant('BUILDING'): {
        this.resetBuildings();
        this.resetFloors();
        this.resetWings();
        break;
      }

      case this.translateService.instant('WING'): {
        this.resetWings();
        break;
      }

      case this.translateService.instant('EVENT_TYPE'): {
        this.resetEventCodes();
        break;
      }
    }
  }

  /**
   * @description: closeDialog function closes the dialogRef.
   * @input none
   * @output { void }
   */
  closeDialog() {
    if (this.getFilterCount() === 0) {
      this.dialogRef?.close();
      return;
    } else {
      const dialogInstance = this.helperService.openModalDialog(
        ConfirmDialogComponent,
        {
          title: this.translateService.instant('TITLE'),
          headingText: this.translateService.instant('HEADING_TEXT'),
          bodyText: this.translateService.instant('BODY_TEXT'),
          footerText: this.translateService.instant('FOOTER_TEXT'),
        },
        CONSTANTS.FIXED_SCREEN_MODAL
      );
      dialogInstance.afterClosed().subscribe((result: boolean) => {
        if (result) {
          this.dialogRef?.close();
        }
      });
    }
  }

  /**
   * @description: navigateBack function set the step to 1.
   * @input none
   * @output { void }
   */
  navigateBack(step: number) {
    step === 0 ? this.closeDialog() : (this.stepper = 0);
  }

  /**
   * @description: #changeStep function set the step to 2 for Wings
   *               and 3 for Floors.
   * @param { string } step
   * @output { void }
   */
  changeStep(step: number) {
    this.stepper = step;
  }

  /**
   * @description: transformListData function transforms the given list by renaming id and name keys.
   * @param {Array} list - List of items.
   * @param {string} idKey - Key representing the id.
   * @param {string} nameKey - Key representing the name.
   * @returns {Array} - Transformed list.
   */
  transformListData<T>(list: T[], idKey: keyof T, nameKey: keyof T): T[] {
    return list?.map((listItem) => ({
      ...listItem,
      id: listItem[idKey],
      name: listItem[nameKey],
      state: false,
    }));
  }

  /**
   * @description: updateItemList function updates the list based on the type.
   * @param {Object} data - Updated data.
   * @param {string} type - Type of the update.
   * @output { void }
   */
  updateItemList(data: any, type: string) {
    switch (type) {
      case CONSTANTS.SELECT_BUILDING: {
        this.updateBuildings(data?.itemList, data?.selectedId);
        break;
      }

      case CONSTANTS.SELECT_FLOOR: {
        this.updateFloors(data?.itemList);
        break;
      }

      case CONSTANTS.SELECT_WING: {
        this.updateWings(data?.itemList);
        break;
      }

      case CONSTANTS.SELECT_EVENT_CODE: {
        this.updateEventCodes(data?.itemList);

        break;
      }
    }
  }

  /**
   * @description Updates the list of selected buildings and their descriptions.
   *
   * @param {IBuilding[]} buildings - The list of buildings.
   * @param {string} buildingId - The ID of the selected building.
   * @returns {void}
   */
  updateBuildings(buildings: IBuilding[], buildingId: string) {
    this.buildingDescription = [];
    this.buildingIds = [];
    this.buildings = buildings;
    this.buildings?.forEach((building: any) => {
      if (building.state === true) {
        this.buildingDescription.push(building.name);
        this.buildingIds.push(building?.id);
        this.fetchAllFloors(this.siteId, buildingId);
      }
      this.resetFloors();
      this.resetWings();
    });
  }

  /**
   * @description Updates the list of selected floors and their descriptions.
   *
   * @param {Floor} floors - The list of floors.
   * @returns {void}
   */
  updateFloors(floors: Floor) {
    this.floorIds = [];
    this.floorDescription = [];
    this.floors = floors;
    this.floors?.forEach((floor: any) => {
      if (floor.state === true) {
        this.floorDescription.push(floor.name);
        this.floorIds.push(floor?.id);
      }
    });
  }

  /**
   * @description Updates the list of selected wings and their descriptions.
   *
   * @param {Floor} floors - The list of floors containing wing information.
   * @returns {void}
   */
  updateWings(floors: Floor) {
    this.wingsDescription = [];
    this.wings = floors;
    this.wings?.forEach((wing: any) => {
      if (wing.state === true) {
        this.wingsDescription.push(wing.name);
      }
    });
  }

  /**
   * @description Updates the list of selected event codes and their descriptions.
   *
   * @param {IEventCodes[]} eventCodes - The list of event codes.
   * @returns {void}
   */
  updateEventCodes(eventCodes: IEventCodes[]) {
    this.eventCodes = eventCodes;
    this.eventCodes?.forEach((event: any) => {
      if (
        event.state === true &&
        !this.eventDescription?.includes(event.name)
      ) {
        this.eventDescription?.push(event.name);
        this.eventCodeIds?.push(event?.id);
      } else if (
        this.eventDescription?.includes(event.name) &&
        event.state === false
      ) {
        this.eventDescription = this.eventDescription?.filter((ev) => {
          return event.name !== ev;
        });

        this.eventCodeIds = this.eventCodeIds?.filter((ev) => {
          return event.id !== ev;
        });
      }
    });
  }

  /**
   * @description: ##fetchAllBuiuldings function is the emitted function from add building
   *              component. On successful creation of building, building list will
   *              be updated and then pass on to the app-building-list component.
   * @returns { void}
   */
  fetchAllBuiuldings(): void {
    this.buildingManagementService
      .getAllBuildingsBySiteId(this.siteId , false)
      .subscribe({
        next: (buildings: any) => {
          this.buildings = this.transformListData(
            buildings,
            'buildingId',
            'buildingName'
          );
          this.filterInitialized = true;
        },
        error: (err: any) => {},
      });
  }

  /**
   * @description: fetchAllFloors function fetches all the floors for a given site and building.
   * @param {string} siteId - Site ID.
   * @param {string} buildingId - Building ID.
   * @output {void}
   */

  fetchAllFloors(siteId: string, buildingId: string) {
    this.floorManagementService
      .getAllFloorsByBuildingId(siteId, buildingId, false)
      .pipe(
        map((floors: any) => {
          this.floors = this.transformListData(floors, 'floorId', 'floorName');
        }),
        tap(() => {
          this.fetchUniqueWings(this.floors);
        }),
        catchError((err: any) => {
          switch (err.message) {
            default: {
              this.floors = [];
              throw new Error(this.translateService.instant('NO_FLOORS_AVAILABLE_IN_BULIDING'));
            }
          }
        })
      )
      .subscribe();
  }

  /**
   * @description: fetchUniqueWings function extracts unique wings from a list of floors.
   * @param {Array} floors - List of floors.
   * @returns {void}
   */
  fetchUniqueWings(floors: any) {
    const uniqueWings: any = [];
    for (const floor of floors) {
      if (!uniqueWings.some((wing: any) => wing.wingName === floor.wingName)) {
        if (floor?.wingName !== null && floor?.wingName !== '') {
          uniqueWings.push({
            wingName: floor.wingName,
            state: false,
          });
        } else {
          let notSpecified: boolean = uniqueWings.some(
            (wing: any) => wing.wingName === this.translateService.instant('NOT_SPECIFIED')
          );
          if (!notSpecified) {
            uniqueWings.push({
              wingName: this.translateService.instant('NOT_SPECIFIED'),
              state: false,
            });
          }
        }
      }
    }

    this.wings = this.transformListData(uniqueWings, 'wingName', 'wingName');
  }

  /**
   * @description Calculates the total count of items in the active issues filter.
   * This count includes the length of buildingDescription, floorDescription, wingsDescription,
   * and eventDescription arrays.
   *
   * @param {IActiveIssuesFilter} activeIssuesFilter - The active issues filter object.
   * @returns {number} The total count of items in the active issues filter.
   */
  getFilterCount(): number {
    return (
      (this?.buildingDescription?.length || 0) +
      (this?.floorDescription?.length || 0) +
      (this?.wingsDescription?.length || 0) +
      (this?.eventDescription?.length || 0)
    );
  }
}
